import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {getOrderStatus, getPriceWithTax, statusMessages} from '../App';
import { billingPolicyIntervals } from '../constants';

type DeliveryDate = {
  current: dayjs.Dayjs;
  change: dayjs.Dayjs;
  min: dayjs.Dayjs;
  max?: dayjs.Dayjs;
  time?: string;
  noDay: boolean;
};
const SubscriptionListDetail = ({ setting, subscription, index }): JSX.Element => {
  type StatusMessage = keyof typeof statusMessages;
  const [deliveryDate, setDeliveryDate] = useState<DeliveryDate>();

  const disableWeek = () => {
    return [
      subscription!.shop.disableDeliveryDaysSun,
      subscription!.shop.disableDeliveryDaysMon,
      subscription!.shop.disableDeliveryDaysTue,
      subscription!.shop.disableDeliveryDaysWed,
      subscription!.shop.disableDeliveryDaysThr,
      subscription!.shop.disableDeliveryDaysFri,
      subscription!.shop.disableDeliveryDaysSat,
    ];
  };
  const nextDeliveryDate = (nextBillingDate: dayjs.Dayjs, deliveryDays: number): dayjs.Dayjs => {
    let nextDelivery = nextBillingDate.add(deliveryDays, 'day');

    if (!disableWeek().every((v) => v)) {
      const disableDays = disableWeek().slice(nextDelivery.day()).concat(disableWeek().slice(0, nextDelivery.day()));
      nextDelivery = nextDelivery.add(
        disableDays.findIndex((v) => !v),
        'days'
      );
    }

    return nextDelivery;
  };

  useEffect(() => {
    const nextBilling = dayjs(subscription.nextBillingDate);
    const purchaseAt = subscription!.billingAttempts.length
      ? subscription!.billingAttempts[0].createdAt
      : subscription!.createdAt;
    const nextDelivery = nextDeliveryDate(
      nextBilling,
      subscription!.deliveryDays === -1 ? subscription!.shop.deliveryDays : subscription!.deliveryDays
    );
    const nextDeliveryLimit = nextDeliveryDate(nextDelivery, subscription!.shop!.deliveryDaysMax);
    const nextBillingLimit = nextBilling.add(
      subscription!.billingPolicyIntervalCount,
      subscription!.billingPolicyInterval.toLowerCase() as 'day' | 'week' | 'month'
    );

    let nextDeliveryMin: dayjs.Dayjs | undefined = nextBilling.add(subscription!.shop!.deliveryDays + 1, 'day');
    let nextDeliveryMax: dayjs.Dayjs | undefined = nextBilling.add(30, 'day');

    if (subscription!.shop!.changeNextBillingDateWithDeliveryDate) {
      nextDeliveryMax =
        subscription!.shop!.deliveryDaysMax === 0
          ? undefined
          : nextBillingLimit.isAfter(nextDeliveryLimit)
          ? nextDeliveryLimit
          : nextBillingLimit;
      nextDeliveryMin = dayjs()
        .add(subscription!.shop.deliveryDays, 'day')
        .isAfter(dayjs(purchaseAt).add(subscription!.shop!.deliveryDays, 'day'))
        ? dayjs().add(subscription!.shop.deliveryDays + 1, 'day')
        : dayjs(purchaseAt).add(subscription!.shop!.deliveryDays, 'day');
    }

    setDeliveryDate({
      current: nextDelivery,
      change: nextDelivery,
      min: nextDeliveryMin,
      max: nextDeliveryMax,
      time: subscription!.deliveryTime,
      noDay: subscription!.deliveryDays === -1 && subscription!.shop.enableDeliveryDateNoDay,
    });
  }, [subscription]);
  const subscriptionSkippedCount =
    subscription.subscriptionHistories?.filter((h) => h.status === 'SKIPPED' && !h.canceledAt)[0]?.skipCount ?? 0;
  const rewindNextBillingDate = dayjs(subscription.nextBillingDate);

  return (
    <>
      <div className="mp-order-history-box" key={index}>
        <div className="mp-status-line">
          <div className="mp-stt-line-title">{getOrderStatus(subscription)}</div>
          <div className="mp-stt-line-info">
            <div className="stt-line-row">
              <div className="stt-lr-ttl large">お申込み番号</div>
              <div className="stt-lr-txt">{subscription.subscriptionContractId?.split('/')[4]}</div>
            </div>
          </div>
        </div>
        <div className="mp-products-box">
          <div className="mp-products-box-left">
            <div className="mp-list-products">
              {subscription.subscriptionLines.map((subscription, index) => (
                  <div className="mp-row-product" key={index}>
                    {
                        subscription.variantImage &&
                        <a href={subscription.onlineStorePreviewUrl} className="mp-row-product-thumb">
                          <img src={subscription.variantImage} alt={subscription.title}/>
                        </a>
                    }
                    <div className="mp-row-product-info">
                      <div className="pro-inf-ttl">{subscription.title}</div>
                      <div className="pro-inf-stt">
                        <div className="stt-row">単価：¥{getPriceWithTax(subscription.currentPriceAmount)} 税込</div>
                        <div className="stt-row">
                          数量：{subscription.quantity}
                        </div>
                      </div>
                      <div className="pro-inf-price">¥{getPriceWithTax(subscription.currentPriceAmount * subscription.quantity)} <small>税込</small></div>
                    </div>
                  </div>
              ))}
            </div>
          </div>
          <div className="mp-products-box-right">
            <div className="mp-total-box">
              <div className="mp-subtotal-box">
                <div className="row-subtotal">
                  <div className="row-subtotal-ttl">お届けサイクル</div>
                  <div className="row-subtotal-txt">{subscription.billingPolicyIntervalCount + billingPolicyIntervals[subscription.billingPolicyInterval]}</div>
                </div>
                <div className="row-subtotal">
                  <div className="row-subtotal-ttl">次回お届け予定日</div>
                  <div className="row-subtotal-txt">{deliveryDate ? deliveryDate!.change.format('YYYY年MM月DD日') : ''}</div>
                </div>
                <div className="row-subtotal">
                  <div className="row-subtotal-ttl">変更締め切り日</div>
                  <div className="row-subtotal-txt">{rewindNextBillingDate.subtract(1, 'd').format('YYYY年MM月DD日 HH:mm')}</div>
                </div>
                <div className="row-subtotal">
                  <div className="row-subtotal-ttl">お支払い金額</div>
                  <div className="row-subtotal-txt">
                    <div className="txt-price">
                      ¥{getPriceWithTax(subscription.subscriptionLines.reduce(
                        (total, line) => total + line.currentPriceAmount * line.quantity,
                        subscription.deliveryPriceAmount
                    ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="mp-button-box">
                <Link to={`/subscriptions/${encodeURIComponent(subscription.subscriptionContractId)}`} className="mp-btn-cm large gray-cm">
                  お申込み内容の詳細・変更
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default SubscriptionListDetail;
